import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/types';
import ResourcedEmoji from '@atlaskit/emoji/resourced-emoji';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { TabPillColorSurface } from '../tab-color/TabPillColorSurface.tsx';
import type { TabOrientation } from '../nativeTabsTypes.ts';
import type { TabColorKey } from '../tab-color/tabColorDefinitions.ts';

// Editor selected-node styles add a border and blanket to any emoji inside the selected extension.
// Tab labels use emoji as decorative pill content, so the pill should own the selected state instead.
const emojiSelectionOverride = `
[data-native-tabs-emoji] [data-emoji-type] {
	box-shadow: none !important;
}

[data-native-tabs-emoji] [data-emoji-type]::before {
	content: none !important;
}
`;

const styles = cssMap({
	editableTitle: {
		cursor: 'text',
	},
	verticalTitle: {
		display: 'block',
		minWidth: 0,
		overflowWrap: 'anywhere',
		textAlign: 'start',
		whiteSpace: 'normal',
	},
	titleInput: {
		backgroundColor: 'transparent',
		borderWidth: 0,
		cursor: 'text',
		fieldSizing: 'content',
		font: token('font.body'),
		margin: token('space.0'),
		minWidth: '32px',
		outline: 'none',
		padding: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Native input needs to inherit the active pill text color.
		color: 'currentColor' as any,
	},
	// Vertical renames stack a hidden copy of the draft and a textarea in one grid cell, so the
	// editor wraps and sizes exactly like the read-only title instead of collapsing to one line.
	verticalTitleEditor: {
		display: 'grid',
		minWidth: 0,
		maxWidth: '100%',
		flex: '1 1 auto',
	},
	verticalTitleSizer: {
		gridColumnStart: 1,
		gridRowStart: 1,
		font: token('font.body'),
		visibility: 'hidden',
	},
	verticalTitleTextarea: {
		gridColumnStart: 1,
		gridRowStart: 1,
		fieldSizing: 'fixed',
		width: 0,
		boxSizing: 'border-box',
		minWidth: '100%',
		overflow: 'hidden',
		overflowWrap: 'anywhere',
		resize: 'none',
		whiteSpace: 'normal',
	},
	emoji: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		marginRight: token('space.075'),
	},
});

export type TabPillProps = {
	/** The visible label of the tab. */
	title: string;
	/** Whether this pill represents the currently active tab. */
	isActive: boolean;
	/** Click handler — selects this tab. */
	onClick: (event: React.SyntheticEvent<HTMLElement>) => void;
	/** Stable id for the pill (sets DOM `id`, derives `aria-controls` and `data-testid`). */
	id: string;
	/**
	 * Roving-tabindex value managed by the parent `TabBar`. Defaults to `0`
	 * so a standalone `TabPill` (rare) is keyboard-reachable.
	 */
	tabIndex?: number;
	/**
	 * When true, adds extra right padding to visually reserve space for the
	 * absolutely-positioned `TabPillMenu` overlay button.
	 */
	showMenu?: boolean;
	/**
	 * Optional emoji id (e.g. `'1f600'`) to display before the tab title.
	 * Resolved to an AtlasKit emoji image via `ResourcedEmoji`.
	 */
	emoji?: string;
	/**
	 * Emoji provider for resolving emoji images. Required when `emoji` is set.
	 */
	emojiProvider?: Promise<EmojiProvider>;
	/**
	 * Called when the user clicks the emoji glyph on an active tab.
	 * Opens the emoji picker to change the emoji.
	 */
	onEmojiClick?: () => void;
	/** Whether the active title can enter inline rename mode. */
	canRename?: boolean;
	/** Whether this pill is currently rendering the inline rename input. */
	isEditing?: boolean;
	/** Whether this pill should preview a destructive tab deletion. */
	isDeletePreviewed?: boolean;
	/** Called when the active title should enter rename mode. */
	onTitleEditStart?: () => void;
	/** Called when the edit draft should be saved. */
	onTitleEditSave?: (
		title: string,
		options?: { shouldRestoreFocus?: boolean; focusVisible?: boolean },
	) => void;
	/** Called when editing should cancel without saving. */
	onTitleEditCancel?: (options?: { focusVisible?: boolean }) => void;
	/**
	 * Optional accent color key. When set, overrides the default neutral styling.
	 */
	color?: TabColorKey;
	/** Tab navigation orientation. */
	orientation?: TabOrientation;
};

/**
 * `TabPill` — a single pressable tab button rendering a tab title with active /
 * inactive visual states. Tab color appearance is delegated to the tab-color
 * surface while this component owns title and emoji presentation.
 */
export const TabPill: React.ForwardRefExoticComponent<
	TabPillProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, TabPillProps>(
	(
		{
			title,
			isActive,
			onClick,
			id,
			tabIndex = 0,
			showMenu = false,
			emoji,
			emojiProvider,
			onEmojiClick,
			canRename = false,
			isEditing = false,
			isDeletePreviewed = false,
			onTitleEditStart,
			onTitleEditSave,
			onTitleEditCancel,
			color,
			orientation = 'horizontal',
		},
		ref,
	) => {
		const emojiRef = useRef<HTMLSpanElement>(null);
		const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
		const isCancellingRef = useRef(false);
		const isCommittingRef = useRef(false);
		// Only show the focus ring when focus returns after a keyboard-started edit.
		const isEditStartedWithKeyboardRef = useRef(false);
		const [draftTitle, setDraftTitle] = useState(title);

		useEffect(() => {
			if (!isEditing) {
				return;
			}
			setDraftTitle(title);
			isCancellingRef.current = false;
			isCommittingRef.current = false;
			inputRef.current?.focus();
			inputRef.current?.select();
		}, [isEditing, title]);

		const handleClick = (event: React.SyntheticEvent<HTMLElement>) => {
			if (
				onEmojiClick &&
				isActive &&
				emojiRef.current &&
				emojiRef.current.contains(event.target as Node)
			) {
				event.stopPropagation();
				onEmojiClick();
				return;
			}
			if (canRename) {
				event.stopPropagation();
				setDraftTitle(title);
				onTitleEditStart?.();
				return;
			}
			onClick(event);
		};

		const handleDraftChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			// Titles are single-line; pasted line breaks into the vertical textarea become spaces.
			setDraftTitle(event.currentTarget.value.replace(/[\r\n]+/g, ' '));
		};

		const handleDraftKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			event.stopPropagation();
			if (event.key === 'Enter') {
				event.preventDefault();
				isCommittingRef.current = true;
				onTitleEditSave?.(event.currentTarget.value, {
					shouldRestoreFocus: true,
					focusVisible: isEditStartedWithKeyboardRef.current,
				});
				return;
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				isCancellingRef.current = true;
				onTitleEditCancel?.({ focusVisible: isEditStartedWithKeyboardRef.current });
			}
		};

		const handleDraftBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (isCommittingRef.current) {
				isCommittingRef.current = false;
				return;
			}
			if (isCancellingRef.current) {
				isCancellingRef.current = false;
				return;
			}
			onTitleEditSave?.(event.currentTarget.value);
		};
		const emojiContent = useMemo(
			() =>
				emoji && emojiProvider ? (
					<>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- ResourcedEmoji does not expose a styling prop for the inner [data-emoji-type] element. */}
						<style>{emojiSelectionOverride}</style>
						<Box ref={emojiRef} xcss={styles.emoji} data-native-tabs-emoji>
							<ResourcedEmoji
								emojiProvider={emojiProvider}
								emojiId={{ id: emoji, shortName: emoji }}
								fitToHeight={20}
								showTooltip={false}
							/>
						</Box>
					</>
				) : null,
			[emoji, emojiProvider],
		);

		return (
			<TabPillColorSurface
				ref={ref}
				id={id}
				color={color}
				isActive={isActive}
				isDeletePreviewed={isDeletePreviewed}
				isEditing={isEditing}
				orientation={orientation}
				showMenu={showMenu}
				tabIndex={tabIndex}
				onClick={(e: React.SyntheticEvent<HTMLElement>) => {
					isEditStartedWithKeyboardRef.current = false;
					handleClick(e);
				}}
				onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						isEditStartedWithKeyboardRef.current = true;
						handleClick(e);
					}
				}}
			>
				{emojiContent}
				{isEditing && orientation === 'vertical' && (
					<Box xcss={styles.verticalTitleEditor}>
						<Box
							as="span"
							aria-hidden="true"
							xcss={cx(styles.verticalTitle, styles.verticalTitleSizer)}
						>
							{/* The trailing space keeps the sizer one line tall when the draft is empty. */}
							{`${draftTitle} `}
						</Box>
						<Box
							as="textarea"
							ref={inputRef}
							rows={1}
							aria-label={title}
							testId={`native-tabs-title-input-${id}`}
							value={draftTitle}
							onChange={handleDraftChange}
							onBlur={handleDraftBlur}
							onKeyDown={handleDraftKeyDown}
							xcss={cx(styles.titleInput, styles.verticalTitleTextarea)}
						/>
					</Box>
				)}
				{isEditing && orientation !== 'vertical' && (
					<Box
						as="input"
						ref={inputRef}
						aria-label={title}
						testId={`native-tabs-title-input-${id}`}
						value={draftTitle}
						onChange={handleDraftChange}
						onBlur={handleDraftBlur}
						onKeyDown={handleDraftKeyDown}
						xcss={styles.titleInput}
					/>
				)}
				{!isEditing && (
					<Box
						as="span"
						xcss={cx(
							canRename && styles.editableTitle,
							orientation === 'vertical' && styles.verticalTitle,
						)}
					>
						{title}
					</Box>
				)}
			</TabPillColorSurface>
		);
	},
);

TabPill.displayName = 'TabPill';
